import { fetchJSON as fetchAPI } from './client';

export type ADRCategory = {
  key: string;
  name: string;
  description?: string;
};

export type TemplateField = {
  key: string;
  name: string;
  description?: string;
  required: boolean;
};

export type MandatoryDecision = {
  key: string;
  title: string;
  category: string;
  description?: string;
  status: string;
  related_docs?: string[];
};

export type GovernanceRule = {
  key: string;
  rule: string;
  description?: string;
  enforced: boolean;
};

export type Deliverable = {
  key: string;
  name: string;
  description?: string;
  status: string;
};

export type EngineeringGuideline = {
  key: string;
  title: string;
  description?: string;
  mandatory: boolean;
};

export type ADRRecord = {
  key: string;
  id: string;
  title: string;
  status: string;
  date: string;
  category: string;
  context: string;
  problem: string;
  decision: string;
  alternatives?: string[];
  pros?: string[];
  cons?: string[];
  risks?: string[];
  mitigations?: string[];
  implementation_notes?: string;
  review_trigger?: string;
  related_adrs?: string[];
  implementation_impact?: string[];
};

export type DecisionIndexEntry = {
  key: string;
  adr_id: string;
  title: string;
  category: string;
  status: string;
  date: string;
};

export type DependencyLink = {
  key: string;
  from_id: string;
  to_id: string;
  link_type: string;
  label?: string;
  related_ids?: string[];
};

export type CrossReference = {
  key: string;
  source_id: string;
  target_doc: string;
  target_type: string;
  relationship: string;
};

export type RepositoryDashboard = {
  master_repo_id: string;
  tenant_id: string;
  version: string;
  applies_to: string[];
  categories: number;
  template_fields: number;
  mandatory_decisions: number;
  governance_rules: number;
  deliverables: number;
  engineering_guidelines: number;
  adr_records: number;
  decision_index: number;
  dependency_links: number;
  cross_references: number;
  generated_at: string;
};

export type RepositoryValidation = {
  valid: boolean;
  categories_defined: boolean;
  template_complete: boolean;
  mandatory_adrs_registered: boolean;
  governance_rules_defined: boolean;
  deliverables_complete: boolean;
  records_seeded: boolean;
  acceptance_passed: boolean;
  gaps?: string[];
  evaluated_at: string;
};

export type ADRMasterStatus = {
  master_repo_id: string;
  version: string;
  applies_to: string[];
  categories: number;
  template_fields: number;
  mandatory_decisions: number;
  governance_rules: number;
  deliverables: number;
  engineering_guidelines: number;
  adr_records: number;
  decision_index: number;
  dependency_links: number;
  cross_references: number;
  engineering_memory: boolean;
  production_ready: boolean;
  status: string;
};

const GOVERNANCE_BASE = import.meta.env.VITE_GOVERNANCE_API ?? '/api/v1/governance';

function fetchGovernance<T>(path: string, init?: RequestInit): Promise<T> {
  return fetchAPI<T>(`${GOVERNANCE_BASE}${path}`, init);
}

export const architectureDecisionRecordsApi = {
  dashboard: () => fetchGovernance<RepositoryDashboard>('/adr/dashboard'),
  categories: () => fetchGovernance<{ categories: ADRCategory[] }>('/adr/categories').then((r) => r.categories),
  template: () => fetchGovernance<{ fields: TemplateField[] }>('/adr/template').then((r) => r.fields),
  mandatoryDecisions: () => fetchGovernance<{ decisions: MandatoryDecision[] }>('/adr/mandatory-decisions').then((r) => r.decisions),
  records: () => fetchGovernance<{ records: ADRRecord[] }>('/adr/records').then((r) => r.records),
  decisionIndex: () => fetchGovernance<{ index: DecisionIndexEntry[] }>('/adr/decision-index').then((r) => r.index),
  dependencyMap: () => fetchGovernance<{ links: DependencyLink[] }>('/adr/dependency-map').then((r) => r.links),
  crossReferences: () => fetchGovernance<{ references: CrossReference[] }>('/adr/cross-references').then((r) => r.references),
  governanceRules: () => fetchGovernance<{ rules: GovernanceRule[] }>('/adr/governance-rules').then((r) => r.rules),
  engineeringGuidelines: () => fetchGovernance<{ guidelines: EngineeringGuideline[] }>('/adr/engineering-guidelines').then((r) => r.guidelines),
  deliverables: () => fetchGovernance<{ deliverables: Deliverable[] }>('/adr/deliverables').then((r) => r.deliverables),
  validate: (attrs: Record<string, boolean>) =>
    fetchGovernance<RepositoryValidation>('/adr/validate', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ attrs }),
    }),
  status: () => fetchGovernance<ADRMasterStatus>('/adr/status'),
};
